import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';
import '@/index.css';

import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { HomePage } from './pages/HomePage';
import { ContentItemPage } from './pages/ContentItemPage';
import ArticleDetailPage from './pages/ArticleDetailPage';
import { NewsPage } from './pages/NewsPage';
import { MagazinePage, MagazineIssuePage } from './pages/MagazinePage';
import { LoginPage } from './pages/LoginPage';
import { AdminDashboard } from './pages/AdminDashboard';
import { AnnouncementBarSettings } from './pages/AnnouncementBarSettings';
import { MagazineList } from './pages/MagazineList';
import { MagazineEditor } from './pages/MagazineEditor';
import { MagazinePageManager } from './pages/MagazinePageManager';
import { ContentLibraryList, ContentLibraryEditor } from './pages/ContentLibrary';
import FirstRoundPage from './pages/FirstRoundPage';
import TheWeekAheadPage from './pages/TheWeekAheadPage';
import NewsroomPage from './pages/NewsroomPage';
import NewsletterPage from './pages/NewsletterPage';
import PodcastPage from './pages/PodcastPage';
import RoundTablePage from './pages/RoundTablePage';
import AdminRoundTablePage from './pages/AdminRoundTablePage';
import SubscribePage from './pages/SubscribePage';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';
import OnlineReaderTestPage, { MagazineReaderV2 } from './pages/OnlineReaderTestPage';
import MagazineArchivePage from './pages/MagazineArchivePage';
// ── Job Board v2 (standalone module) ──────────────────────────────────────
import CareerHubPageNew from './job-board/pages/CareerHubPage';
import JobDetailPage from './job-board/pages/JobDetailPage';
// ── Employer Portal (Step 9) ──────────────────────────────────────────────
import EmployerAuthPage from './job-board/pages/EmployerAuthPage';
import EmployerDashboardPage from './job-board/pages/EmployerDashboardPage';
import EmployerJobsPage from './job-board/pages/EmployerJobsPage';
import EmployerProfilePage from './job-board/pages/EmployerProfilePage';
import ApplicantPipelinePage from './job-board/pages/ApplicantPipelinePage';
// ── Seeker Portal (Step 10) ───────────────────────────────────────────────
import SeekerAuthPage from './job-board/pages/SeekerAuthPage';
import SeekerDashboardPage from './job-board/pages/SeekerDashboardPage';
import SeekerProfilePage from './job-board/pages/SeekerProfilePage';
// ── First Briefcase (Step 11) ─────────────────────────────────────────────
import LegacyFirstBriefcasePage from './pages/FirstBriefcasePage';
import FirstBriefcasePage from './job-board/pages/FirstBriefcasePage';
// ── Legacy career hub kept for reference (routes replaced below) ───────────
import CareerHubPage from './pages/CareerHubPage';
import CareerHubDetailPage from './pages/CareerHubDetailPage';
import PostJobPage from './pages/PostJobPage';
import AdminJobsPage from './pages/AdminJobsPage';
import AdminJobPlansPage from './pages/AdminJobPlansPage';
import EducationPage from './pages/EducationPage';
import HealthcarePage from './pages/HealthcarePage';
import WorkforceLaborPage from './pages/WorkforceLaborPage';
import GovernmentPage from './pages/GovernmentPage';
import TechnologyAIPage from './pages/TechnologyAIPage';
import BusinessFinancePage from './pages/BusinessFinancePage';
// New CMS pages
import AdminNewsletterPage from './pages/AdminNewsletterPage';
import AdminContactPage from './pages/AdminContactPage';
import AdminLeadsPage from './pages/AdminLeadsPage';
import AdminTickerPage from './pages/AdminTickerPage';
import AdminVideoManagerPage from './pages/AdminVideoManagerPage';
import AdminMegaMenuPage from './pages/AdminMegaMenuPage';
import AdminHeroImagesPage from './pages/AdminHeroImagesPage';
import AdminJobBoardPage from './pages/AdminJobBoardPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminStubPage from './pages/AdminStubPage';
import ArticleEngineReview from './pages/ArticleEngineReview';
import MediaKitPage from './pages/MediaKitPage';
import AdminMediaKitPage from './pages/AdminMediaKitPage';
import BrochureAccess from './pages/BrochureAccess';
import ExemplaryLeadersModal from './components/ExemplaryLeadersModal';
import OrganizationInFocusModal from './components/OrganizationInFocusModal';
import AnnouncementBar from './components/AnnouncementBar';

// Scroll to top on every route change; skip when navigating to an anchor link.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function App() {
  const [showElModal, setShowElModal] = useState(false);
  const [showOIFModal, setShowOIFModal] = useState(false);

  useEffect(() => {
    const elHandler  = () => setShowElModal(true);
    const oifHandler = () => setShowOIFModal(true);
    window.addEventListener('open-el-modal',  elHandler);
    window.addEventListener('open-oif-modal', oifHandler);
    return () => {
      window.removeEventListener('open-el-modal',  elHandler);
      window.removeEventListener('open-oif-modal', oifHandler);
    };
  }, []);

  return (
    <div className="App">
      {showElModal && ReactDOM.createPortal(
        <ExemplaryLeadersModal onClose={() => setShowElModal(false)} />,
        document.body
      )}
      {showOIFModal && ReactDOM.createPortal(
        <OrganizationInFocusModal onClose={() => setShowOIFModal(false)} />,
        document.body
      )}
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Admin Routes */}
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/settings" element={<AnnouncementBarSettings />} />
          <Route path="/admin/magazine" element={<MagazineList />} />
          <Route path="/admin/magazine/new" element={<MagazineEditor />} />
          <Route path="/admin/magazine/:id/edit" element={<MagazineEditor />} />
          <Route path="/admin/magazine/:id/pages" element={<MagazinePageManager />} />
          <Route path="/admin/content" element={<ContentLibraryList />} />
          <Route path="/admin/content/new" element={<ContentLibraryEditor />} />
          <Route path="/admin/content/:id/edit" element={<ContentLibraryEditor />} />
          <Route path="/admin/article-engine" element={<ArticleEngineReview />} />
          <Route path="/admin/jobs" element={<AdminJobsPage />} />
          <Route path="/admin/job-plans" element={<AdminJobPlansPage />} />
          <Route path="/admin/first-briefcase" element={<AdminStubPage title="First Briefcase" section="Career Hub" description="CMS controls for the First Briefcase landing page — hero image, partner content, and section copy. Coming in the next build pass." eta="Q3 2026" />} />
          <Route path="/admin/round-table" element={<AdminRoundTablePage />} />
          {/* New CMS routes */}
          <Route path="/admin/newsletter" element={<AdminNewsletterPage />} />
          <Route path="/admin/subscribers" element={<AdminNewsletterPage />} />
          <Route path="/admin/contacts" element={<AdminContactPage />} />
          <Route path="/admin/leads" element={<AdminLeadsPage />} />
          <Route path="/admin/media-kit" element={<AdminMediaKitPage />} />
          <Route path="/admin/ticker" element={<AdminTickerPage />} />
          <Route path="/admin/videos" element={<AdminVideoManagerPage />} />
          <Route path="/admin/mega-menu" element={<AdminMegaMenuPage />} />
          <Route path="/admin/hero-images" element={<AdminHeroImagesPage />} />
          <Route path="/admin/job-board"  element={<AdminJobBoardPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/premium-jobs" element={<AdminStubPage title="Premium Jobs" section="Career Hub" description="A filtered view of featured and premium Career Hub listings. Coming in the next build pass." eta="Q3 2026" />} />
          <Route path="/admin/first-round" element={<AdminStubPage title="First Round" section="Editorial" description="CMS control for the First Round daily briefing widget — headline, audio, and read link. Coming in the next build pass." eta="Q3 2026" />} />
          <Route path="/admin/widgets" element={<AdminStubPage title="Widget Inventory" section="Site Controls" description="Manage reusable content widgets placed across the site. Coming in a future update." eta="Q4 2026" />} />
          <Route path="/admin/placements" element={<AdminStubPage title="Placement Slots" section="Site Controls" description="Configure editorial placement zones across homepage, sector pages, and newsletter. Coming in a future update." eta="Q4 2026" />} />
          <Route path="/admin/sponsors" element={<AdminStubPage title="Sponsors" section="Marketing" description="Sponsor profile management and campaign tracking. Coming in a future update." eta="Q4 2026" />} />
          <Route path="/admin/exemplary" element={<AdminStubPage title="Exemplary Leaders" section="Directory" description="Manage the Exemplary Leaders directory — profiles, sectors, and featured spotlights." eta="Q3 2026" />} />
          <Route path="/admin/institutions" element={<AdminStubPage title="Institutions / Organizations" section="Directory" description="Manage the institutional directory — higher ed, government agencies, and enterprise organizations." eta="Q3 2026" />} />
          <Route path="/admin/focus-areas" element={<AdminStubPage title="Focus Areas" section="Directory" description="Manage focus area taxonomy used across coverage, Career Hub, and editorial." eta="Q3 2026" />} />
          <Route path="/admin/site-settings" element={<AdminStubPage title="Site Settings" section="System" description="Global site configuration — SEO defaults, social meta, domain aliases, and environment flags." eta="Q3 2026" />} />
          <Route path="/admin/media" element={<AdminStubPage title="Media / ImageKit" section="System" description="Browse, upload, and manage all media assets via the ImageKit integration." eta="Q3 2026" />} />
          <Route path="/admin/*" element={<AdminStubPage title="Admin Feature" section="System" description="This section is under active development." />} />

          {/* ── Brochure access — standalone redirect, no Header/Footer ── */}
          <Route path="/brochure/access" element={<BrochureAccess />} />

          {/* Isolated full-viewport routes (no Header/Footer) */}
          <Route path="/online-reader-test" element={<OnlineReaderTestPage />} />

          {/* ── Employer Portal (Step 9) — standalone, no Header/Footer ── */}
          <Route path="/employer/login"    element={<EmployerAuthPage initialTab="login" />} />
          <Route path="/employer/register" element={<EmployerAuthPage initialTab="register" />} />
          <Route path="/employer/dashboard" element={<EmployerDashboardPage />} />
          <Route path="/employer/jobs"     element={<EmployerJobsPage />} />
          <Route path="/employer/profile"  element={<EmployerProfilePage />} />
          <Route path="/employer/jobs/:job_id/applicants" element={<ApplicantPipelinePage />} />

          {/* ── Seeker Portal (Step 10) — standalone, no Header/Footer ── */}
          <Route path="/seeker/login"     element={<SeekerAuthPage initialTab="login" />} />
          <Route path="/seeker/register"  element={<SeekerAuthPage initialTab="register" />} />
          <Route path="/seeker/dashboard" element={<SeekerDashboardPage />} />
          <Route path="/seeker/profile"   element={<SeekerProfilePage />} />

          {/* ── First Briefcase landing page (original) ── */}
          <Route path="/first-briefcase"  element={<><AnnouncementBar /><Header /><main><LegacyFirstBriefcasePage /></main><Footer /></>} />
          {/* ── Round Table — fullscreen coming soon, no layout ── */}
          <Route path="/round-table" element={<><AnnouncementBar /><Header /><RoundTablePage /></>} />
          {/* ── Career Assessment intake form (4-step) ── */}
          <Route path="/career-assessment" element={<FirstBriefcasePage />} />
          {/* Archive page: public, with Header/Footer — must be before /magazine/:slug */}
          <Route path="/magazine/archive" element={<><Header /><main><MagazineArchivePage /></main><Footer /></>} />
          <Route path="/magazine/:slug" element={<MagazineReaderV2 />} />

          {/* Public Routes */}
          <Route path="*" element={
            <>
              <AnnouncementBar />
              <Header />
              <main>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/article/:slug" element={<ContentItemPage />} />
                  <Route path="/insights/:slug" element={<ArticleDetailPage />} />
                  <Route path="/news" element={<NewsPage />} />
                  <Route path="/news/:category" element={<NewsPage />} />
                  <Route path="/opinion" element={<NewsPage />} />
                  <Route path="/magazine" element={<MagazinePage />} />
                  
                  {/* Foundation pages */}
                  <Route path="/first-round" element={<FirstRoundPage />} />
                  <Route path="/the-week-ahead" element={<TheWeekAheadPage />} />
                  <Route path="/newsroom" element={<NewsroomPage />} />
                  <Route path="/newsletter" element={<NewsletterPage />} />
                  <Route path="/podcast" element={<PodcastPage />} />
                  <Route path="/subscribe" element={<SubscribePage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/media-kit" element={<MediaKitPage />} />

                  {/* Career Hub — isolated public marketplace */}
                  {/* ── Job Board v2 routes ── */}
                  <Route path="/career-hub" element={<CareerHubPageNew />} />
                  <Route path="/career-hub/:job_id" element={<JobDetailPage />} />
                  {/* Legacy routes kept for safety */}
                  <Route path="/career-hub/post" element={<PostJobPage />} />
                  
                  {/* Placeholder routes */}
                  <Route path="/resources" element={<PlaceholderPage title="Resources" />} />
                  <Route path="/advertise" element={<PlaceholderPage title="Advertise" />} />
                  <Route path="/privacy" element={<PlaceholderPage title="Privacy Policy" />} />
                  <Route path="/terms" element={<PlaceholderPage title="Terms of Service" />} />
                  <Route path="/opportunity-signal" element={<PlaceholderPage title="The Opportunity Signal" />} />
                  {/* Coverage stub routes — full pillar pages are a future build */}
                  <Route path="/coverage/careers" element={<CoverageStubPage pillar="Careers & Jobs" />} />
                  <Route path="/coverage/education" element={<EducationPage />} />
                  <Route path="/coverage/business-finance" element={<BusinessFinancePage />} />
                  <Route path="/coverage/healthcare" element={<HealthcarePage />} />
                  <Route path="/coverage/government" element={<GovernmentPage />} />
                  <Route path="/coverage/technology-ai" element={<TechnologyAIPage />} />
                  <Route path="/coverage/workforce-labor" element={<WorkforceLaborPage />} />
                  {/* 404 — no public route matched */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </main>
              <Footer />
            </>
          } />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

const NotFoundPage = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', background: 'var(--op-paper)' }} data-testid="not-found-page">
    <div style={{ textAlign: 'center', maxWidth: 480 }}>
      <div style={{ fontSize: 72, fontWeight: 900, color: 'var(--op-navy)', lineHeight: 1, marginBottom: 12, letterSpacing: '-0.04em', fontFamily: "'Inter Tight', sans-serif" }}>404</div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--op-ink)', marginBottom: 10, fontFamily: "'Inter Tight', sans-serif" }}>Page not found.</h1>
      <p style={{ color: 'var(--op-muted)', fontSize: 14, lineHeight: 1.7, marginBottom: 28 }}>
        This URL doesn't exist. It may have been removed, renamed, or never published.
      </p>
      <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--op-navy)', color: '#fff', borderRadius: 10, padding: '10px 22px', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
        Back to Opportunity Post
      </Link>
    </div>
  </div>
);

const PlaceholderPage = ({ title }) => (
  <div className="py-24" data-testid="placeholder-page">
    <div className="container-custom text-center">
      <h1 className="text-4xl font-bold mb-4">{title}</h1>
      <p className="text-gray-600">This page is under construction.</p>
    </div>
  </div>
);

const CoverageStubPage = ({ pillar }) => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', background: 'var(--op-paper)' }}>
    <div style={{ textAlign: 'center', maxWidth: 520 }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--op-teal-soft)', borderRadius: 999, padding: '4px 12px', marginBottom: 18 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--op-teal)', letterSpacing: '.06em', textTransform: 'uppercase' }}>Coverage</span>
      </div>
      <div style={{ fontFamily: "'Inter Tight', Inter, sans-serif", fontSize: 32, fontWeight: 900, color: 'var(--op-navy)', letterSpacing: '-.06em', marginBottom: 12 }}>{pillar}</div>
      <p style={{ color: 'var(--op-muted)', fontSize: 15, lineHeight: 1.6, marginBottom: 24 }}>
        The full {pillar} coverage pillar is coming soon. Browse the latest editorial content in the meantime.
      </p>
      <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--op-teal)', color: '#fff', borderRadius: 10, padding: '10px 22px', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
        ← Back to Home
      </Link>
    </div>
  </div>
);

const PlaceholderAdminPage = ({ title }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="admin-placeholder">
    <div className="text-center">
      <h1 className="text-3xl font-bold mb-4">{title}</h1>
      <p className="text-gray-600 mb-6">This admin feature is under development.</p>
      <a href="/admin/dashboard" className="text-[#0E2E59] underline">Back to Dashboard</a>
    </div>
  </div>
);

export default App;